"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Bridge() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const avatars = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/student_talent_one.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/student_talent_one.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/student_talent_one.png-wvc-sizes",
    alt: "Emerging talent portrait one",
    fallback: "ET"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/community_leader_two.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/community_leader_two.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/community_leader_two.webp-wvc-sizes",
    alt: "Community leader portrait two",
    fallback: "CL"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/graduate_talent_three.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/graduate_talent_three.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/graduate_talent_three.png-wvc-sizes",
    alt: "Graduate talent portrait three",
    fallback: "GT"
  }];
  return <section data-section-id="13789" id="the-bridge-between-need-and-talent" className="relative overflow-hidden bg-background py-32 md:py-48 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/3 -left-1/4 h-[80vh] w-[80vh] rounded-lg opacity-70 blur-3xl" style={{
        background: "radial-gradient(circle at center, hsl(199 89% 85% / 0.6), transparent 60%)",
        animation: "hazeDriftA 24s ease-in-out infinite"
      }} aria-hidden="true" />
        <div className="absolute top-1/4 -right-1/4 h-[75vh] w-[75vh] rounded-lg opacity-60 blur-3xl" style={{
        background: "radial-gradient(circle at center, hsl(142 71% 85% / 0.5), transparent 60%)",
        animation: "hazeDriftB 24s ease-in-out infinite"
      }} aria-hidden="true" />
        <div className="absolute -bottom-1/3 left-1/4 h-[70vh] w-[70vh] rounded-lg opacity-55 blur-3xl" style={{
        background: "radial-gradient(circle at center, hsl(199 89% 90% / 0.6), transparent 60%)",
        animation: "hazeDriftC 24s ease-in-out infinite"
      }} aria-hidden="true" />
      </div>

      <style>{`
        @keyframes hazeDriftA {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(8%, 6%) scale(1.08); }
        }
        @keyframes hazeDriftB {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-6%, 4%) scale(1.05); }
        }
        @keyframes hazeDriftC {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(4%, -5%) scale(1.07); }
        }
      `}</style>

      <div ref={ref} className="mx-auto flex max-w-[880px] flex-col items-center px-6 text-center md:px-10 lg:px-16">
        {}
        <div data-index={0} className="transition-all duration-700 ease-out" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transitionDelay: "0ms"
      }}>
          <span className="font-mono font-medium uppercase tracking-[0.12em] text-accent-foreground !text-base"><br />Elevate SoCial Outcomes<br /></span>
        </div>

        <div className="my-8 w-full max-w-2xl transition-all duration-700 ease-out" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transitionDelay: "40ms"
      }}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/IMG_1562.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/IMG_1562.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/IMG_1562.png-wvc-sizes" alt="Community Capability Development Model" className="mx-auto h-auto rounded-lg shadow-md !w-[100px]" loading="lazy" />
        </div>

        {}
        <h1 className="mt-6 font-default font-medium !text-3xl !text-[rgba(74,222,128,1)]" style={{
        fontSize: "clamp(2.75rem, 6.2vw, 5.25rem)",
        lineHeight: 1.02,
        letterSpacing: "-0.025em",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: "80ms"
      }}>Young people need opportunity.&nbsp;&nbsp;<br />Charities need help.&nbsp;&nbsp;<br />Let's bring them together</h1>

        {}
        <p className="mt-7 max-w-[56ch] font-default text-muted-foreground !text-left" style={{
        fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
        lineHeight: 1.55,
        letterSpacing: "-0.005em",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: "160ms"
      }}>Many charities are struggling to find the time, skills and resources to tackle important projects.&nbsp;<span data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:gob8g8" data-ast-start="5091" data-ast-end="5411" style={{
          fontFamily: 'var(--font-default)',
          fontSize: 'clamp(1.0625rem, 1.2vw, 1.25rem)',
          letterSpacing: '-0.005em'
        }}>At the same time, many talented young people are looking for meaningful experience that will help them build confidence, develop skills and find work.</span><div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:3pc0xb" data-ast-start="5411" data-ast-end="5582" contenteditable="false" style={{}}>It feels like these two challenges should be connected.<br /><br /><br />The idea behind Elevate Social Outcomes (ESO) began several years ago whilst supporting a charity through Pilotlight and later working with charities and higher education institutions.<div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:37m5rs" data-ast-start="6580" data-ast-end="6733">A recurring challenge kept appearing.</div><div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:36lpal" data-ast-start="6733" data-ast-end="6986" contenteditable="false" style={{}}>Many organisations had important projects they wanted to deliver but lacked the time, capacity or specialist skills to move them forward.</div><div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:35euy4" data-ast-start="6986" data-ast-end="7468" contenteditable="false" style={{}}>At the same time, talented students and emerging professionals were looking for meaningful opportunities to gain experience and develop their skills.<br /><br />Elevate Social Outcomes was created to test a simple question:<div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:2ro3tm" data-ast-start="7381" data-ast-end="7546">Could these two challenges help solve each other?</div><div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:2qmyvi" data-ast-start="7546" data-ast-end="7734">The projects featured below are early examples of that idea in practice.</div><div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:2pk25o" data-ast-start="7734" data-ast-end="7856"><br /></div></div><div data-ast-id="f:/src/pages/Home/sections/Bridge.tsx|sig:2r5g0d" data-ast-start="7468" data-ast-end="7590" style={{}}><br /></div></div></p>

        {}
        <div className="mt-10 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-5" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: "240ms"
      }}>
          <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive has-[>svg]:px-4 h-auto rounded-lg bg-accent-sky px-7 py-3.5 text-base font-medium shadow-lg transition-all duration-200 ease-out !text-[rgba(251,146,60,1)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-accent-sky/90 hover:shadow-xl">
            
          </Button>

          
        </div>

        {}
        
      </div>
    </section>;
}